import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  MICRONUTRIENTS,
  NUTRIENT_INFO,
  PACKAGE_NUTRIENTS,
  addDays,
  nutrientStatus,
} from '@kraftwerk/shared';
import type { NutrientKey, NutrientStatus, Nutrients } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalFoodLogEntry } from '../../db/local.js';
import { useRememberedOpen } from '../../hooks/useRememberedOpen.js';

/**
 * Ballaststoffe, Zucker, Salz — eingeklappt unter den Makros (20.10.2026),
 * je Gerät gemerkt. Zu zeigt die Zeile nur ihren Namen; die Werte stehen
 * einen Tipp entfernt, statt die Makro-Kachel zu verdoppeln.
 */
export function DayNutrients({
  entries,
  framed = false,
}: {
  entries: LocalFoodLogEntry[];
  /** Eigene Kachel — ohne Zielwerte gibt es keine Makro-Kachel, in der sie steht. */
  framed?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useRememberedOpen(PACKAGE_OPEN_KEY, false);
  // Auch ohne Einträge: 0 steht da wie bei den Makros, statt dass die Zeile fehlt.
  const values = entries.map((entry) => entry.nutrients ?? null);
  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      className={framed ? 'kw-tile' : 'border-t border-line pt-3'}
    >
      <summary className="cursor-pointer text-xs font-medium text-ink-muted">
        {t('nutrients.dayHeading')}
      </summary>
      <div className="mt-2">
        <NutrientRow keys={PACKAGE_NUTRIENTS} values={values} days={1} compact />
      </div>
    </details>
  );
}

const PACKAGE_OPEN_KEY = 'kraftwerk:package-nutrients-open';
const MICROS_OPEN_KEY = 'kraftwerk:micros-open';

/**
 * Vitamine und Mineralstoffe (18.10.2026): immer da, ausklappbar, alle —
 * vorher musste man sie erst einzeln einschalten und fand sie ganz unten.
 * Tag zeigt die Einträge dieses Tages, Woche den Schnitt der letzten sieben
 * Tage mit Einträgen (ein einzelner Tag schwankt bei Vitaminen stark).
 */
export function DayMicros({
  date,
  entries,
}: {
  date: string;
  entries: LocalFoodLogEntry[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useRememberedOpen(MICROS_OPEN_KEY, false);
  const [range, setRange] = useState<'day' | 'week'>('day');
  const from = addDays(date, -6);
  const weekRows = useLiveQuery(
    async () =>
      range === 'week' && open
        ? (await db.food_log.where('date').between(from, date, true, true).toArray()).filter(
            (row) => !row.deletedAt,
          )
        : [],
    [from, date, range, open],
    [] as LocalFoodLogEntry[],
  );
  const rows = range === 'day' ? entries : weekRows;
  const days = range === 'day' ? 1 : new Set(rows.map((row) => row.date)).size;

  return (
    <details
      className="kw-tile"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="kw-kicker cursor-pointer">{t('nutrients.microsWeekHeading')}</summary>
      <div className="mt-3 flex flex-col gap-2">
        <div className="flex gap-1" role="group" aria-label={t('nutrients.rangeLabel')}>
          {(['day', 'week'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={range === option}
              onClick={() => setRange(option)}
              className="kw-toggle"
            >
              {t(`nutrients.range.${option}`)}
            </button>
          ))}
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-ink-muted">
            {range === 'day' ? t('nutrients.noEntriesDay') : t('nutrients.noEntries')}
          </p>
        ) : (
          <>
            {range === 'week' ? (
              <p className="text-xs text-ink-muted">
                {t('nutrients.averageOver', { count: days })}
              </p>
            ) : null}
            <NutrientRow
              keys={MICRONUTRIENTS}
              values={rows.map((row) => row.nutrients ?? null)}
              days={days}
            />
          </>
        )}
        <p className="text-xs text-ink-muted">{t('nutrients.nrvNote')}</p>
      </div>
    </details>
  );
}

function NutrientRow({
  keys,
  values,
  days,
  compact = false,
}: {
  keys: readonly NutrientKey[];
  values: (Nutrients | null)[];
  days: number;
  compact?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState<NutrientKey | null>(null);
  const statuses = keys.map((key) => nutrientStatus(key, values, days));
  const detail = statuses.find((status) => status.key === open);
  return (
    <div className="flex flex-col gap-2">
      <ul className="grid grid-cols-3 gap-2">
        {statuses.map((status) => (
          <li key={status.key}>
            <NutrientSymbol
              status={status}
              open={open === status.key}
              compact={compact}
              onToggle={() => setOpen(open === status.key ? null : status.key)}
            />
          </li>
        ))}
      </ul>
      {detail ? <NutrientDetail status={detail} days={days} /> : null}
    </div>
  );
}

function NutrientSymbol({
  status,
  open,
  compact,
  onToggle,
}: {
  status: NutrientStatus;
  open: boolean;
  compact: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const info = NUTRIENT_INFO[status.key];
  const name = t(`nutrients.name.${status.key}`);
  const r = 18;
  const length = 2 * Math.PI * r;
  const share = Math.min(1, status.share ?? 0);
  const color =
    status.level === 'above'
      ? 'var(--color-warn)'
      : status.level === 'reached'
        ? 'var(--color-good)'
        : 'var(--color-ink-muted)';
  const amount =
    status.amount === null
      ? '?'
      : `${status.complete ? '' : '≥ '}${format(status.amount, i18n.language)}`;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={t('nutrients.symbolLabel', {
        name,
        amount: status.amount === null ? t('nutrients.unknown') : `${amount} ${info.unit}`,
        reference: `${format(info.reference, i18n.language)} ${info.unit}`,
      })}
      className="flex min-h-14 w-full flex-col items-center gap-1 rounded-xl px-1 py-1"
    >
      <svg viewBox="0 0 44 44" className={compact ? 'size-8' : 'size-11'} aria-hidden="true">
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          strokeWidth="5"
          strokeDasharray={status.amount === null ? '3 4' : undefined}
          style={{ stroke: 'var(--color-surface-raised)' }}
        />
        {share > 0 ? (
          <circle
            cx="22"
            cy="22"
            r={r}
            fill="none"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${length * share} ${length}`}
            transform="rotate(-90 22 22)"
            className="kw-ring"
            style={{ stroke: color }}
          />
        ) : null}
        {status.amount === null ? (
          <text
            x="22"
            y="27"
            textAnchor="middle"
            fontSize="14"
            style={{ fill: 'var(--color-ink-muted)' }}
          >
            ?
          </text>
        ) : null}
      </svg>
      <span className="text-xs font-medium">{name}</span>
      <span className="text-xs text-ink-muted">
        {amount}/{format(info.reference, i18n.language)} {info.unit}
      </span>
    </button>
  );
}

function NutrientDetail({
  status,
  days,
}: {
  status: NutrientStatus;
  days: number;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const info = NUTRIENT_INFO[status.key];
  return (
    <p role="status" className="rounded-lg border border-line px-3 py-2 text-xs text-ink-muted">
      {status.amount === null
        ? t('nutrients.detailUnknown', { name: t(`nutrients.name.${status.key}`) })
        : t(days > 1 ? 'nutrients.detailAverage' : 'nutrients.detail', {
            amount: format(status.amount, i18n.language),
            unit: info.unit,
            reference: format(info.reference, i18n.language),
          })}{' '}
      {t(`nutrients.kind.${info.kind}`)} {t(`nutrients.source.${info.source}`)}
      {status.amount !== null && !status.complete
        ? ` ${t('nutrients.coverage', { known: status.known, entries: status.entries })}`
        : ''}
      {status.key === 'sugar' ? ` ${t('nutrients.sugarNote')}` : ''}
    </p>
  );
}

function format(value: number, language: string): string {
  return value.toLocaleString(language, { maximumFractionDigits: value < 10 ? 1 : 0 });
}
